import { useEffect, useRef, useState } from "react";
import { Play, Loader2 } from "lucide-react";
import apiClient from "@/lib/api-client";

/**
 * Audio de WhatsApp dentro de una burbuja del inbox (15-sep-2026). El binario
 * vive en los servidores de Meta y el backend lo sirve autenticado, así que
 * un <audio src> directo no vale (la etiqueta no manda el Bearer): al pulsar
 * ▶ se descarga como blob y se reproduce desde un objectURL. Carga bajo
 * demanda a propósito — abrir un hilo largo no debe disparar N descargas.
 * Si Meta ya no lo retiene (~30 días), el backend responde 404 y aquí se
 * enseña el aviso.
 */
export default function ConversationAudioPlayer({ mediaUrl }: { mediaUrl: string }) {
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const load = () => {
    setState("loading");
    apiClient
      .get<Blob>(mediaUrl, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        urlRef.current = url;
        setObjectUrl(url);
        setState("ready");
      })
      .catch(() => setState("error"));
  };

  if (state === "ready" && objectUrl) {
    return (
      <audio controls autoPlay src={objectUrl} className="h-10 max-w-full min-w-[200px]" />
    );
  }
  if (state === "error") {
    return (
      <span className="text-xs italic opacity-75">
        Audio no disponible (WhatsApp lo conserva unos 30 días)
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={load}
      disabled={state === "loading"}
      className="flex items-center gap-2 text-sm font-medium underline-offset-2 hover:underline disabled:opacity-60"
    >
      {state === "loading" ? (
        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
      ) : (
        <Play className="w-4 h-4 flex-shrink-0" />
      )}
      {state === "loading" ? "Cargando audio…" : "Escuchar audio"}
    </button>
  );
}
